import {
  OrganizationDetailFragment,
  useMutationNotifier,
  useOrgDashboardQuery,
} from '@alice-financial/alice-sdk'
import { fetchRest, useMutationWithInvalidation, UseRestMutationCallbacks } from '@alice-financial/api'
import { QueryKey } from '@tanstack/react-query'
import { useIntl } from 'react-intl'
import { useOrgId } from '../useOrgId'
import { useUpdateOrganizationLogo } from '../organization/useUpdateOrganizationLogo'

type WorkLocationLogoSubmitValues = {
  logo: File | null
  id: number
}

const submitWorkLocationLogo = ({ logo, id }: WorkLocationLogoSubmitValues) => {
  let body: FormData | string
  const headers: HeadersInit = {}

  if (logo === null) {
    body = JSON.stringify({ logo: null })
    headers['Content-Type'] = 'application/json'
  } else {
    const formData = new FormData()
    formData.append('logo', logo)
    body = formData
    // No need to set Content-Type for FormData, browser will set it automatically
  }

  return fetchRest(`/api/work_locations/${id}`, {
    method: 'PATCH',
    body,
    headers,
  })
}

type UseSubmitWorkLocationLogoMutationOptions = UseRestMutationCallbacks<
  unknown,
  WorkLocationLogoSubmitValues
>

export const useUpdateWorkLocationLogo = (
  org: OrganizationDetailFragment,
  mutationOptions: UseSubmitWorkLocationLogoMutationOptions = {},
  additionalInvalidationKeys: QueryKey[] = []
) => {
  const intl = useIntl()
  const orgId = useOrgId()

  const { mutateAsync: updateOrganizationLogo } = useUpdateOrganizationLogo()

  const mutationOptionsWithNotification = useMutationNotifier(
    {
      onMutate: intl.formatMessage({ id: 'organization.onboarding.locations.submission_notice' }),
      onSuccess: false,
    },
    mutationOptions
  )

  // Include the org dashboard query invalidation key along with any additional keys
  const invalidationKeys: QueryKey[] = [useOrgDashboardQuery.getKey({ orgId }), ...additionalInvalidationKeys]

  return useMutationWithInvalidation(
    ({ logo, id }: WorkLocationLogoSubmitValues) => {
      const orgHasLogo = Boolean(org.logo)
      const orgHasOneLocation = (org.workLocations?.length || 0) + (id ? 1 : 0) === 1
      // set the logo if it's not set, or if there is only one location and its logo is changing
      const mustSetOrgLogo = !orgHasLogo || orgHasOneLocation
      if (mustSetOrgLogo && logo !== null) {
        updateOrganizationLogo({ logo, id: org.id })
      }
      return submitWorkLocationLogo({ logo, id })
    },
    mutationOptionsWithNotification,
    invalidationKeys
  )
}
